import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { Layout } from "./components/layout/Layout";
import { ProtectedRoute } from "./components/common/ProtectedRoute";
import { LoginPage } from "./pages/LoginPage";
import { HomePage } from "./pages/HomePage";
import { BatchesPage } from "./pages/BatchesPage";
import { JournalPostingPage } from "./pages/JournalPostingPage";
import { JournalSnapshotPage } from "./pages/JournalSnapshotPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SagePage } from "./pages/SagePage";
import { WpgFxConverterPage } from "./pages/WpgFxConverterPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Public route */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <HomePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/batches"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BatchesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/journal/:batchName"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <JournalPostingPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/journal/snapshot/:batchName"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <JournalSnapshotPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sage"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SagePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wpg-fx-converter"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WpgFxConverterPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute allowedRoles={["Administrator"]}>
                    <Layout>
                      <SettingsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: "#363636",
                  color: "#fff",
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: "#10b981",
                    secondary: "#fff",
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: "#ef4444",
                    secondary: "#fff",
                  },
                },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
